import React, { useCallback } from 'react';
import Main from '../../components/layouts/Main';
import { Avatar, Badge, Button, Col, Input, Popconfirm, Row, Select, Space, Spin, Tag } from 'antd';
import UserService from '../../services/UserService';
import { catchError } from '../../services/DaoService';
import Toast from '../../helpers/Toast';
import { DeleteFilled, EditFilled, EyeFilled, LockFilled, PlusCircleFilled, ReloadOutlined, UnlockFilled, UsergroupAddOutlined } from '@ant-design/icons';
import UserEdit from '../../components/user/UserEdit';
import { useNavigate } from 'react-router-dom';
import Moment from 'react-moment';
import UnauthorizedMessage from '../../components/utils/UnauthorizedMessage';
import InfiniteTable from '../../components/utils/InfiniteTable';
import { GroupeService } from '../../services/GroupeService';
import DisableUserModal from '../../components/user/DisableUserModal';
import PageTitle from '../../components/utils/PageTitle';
import { useUserContext } from '../../contexts/UserContext';
import Breadcrumb from '../../components/utils/Breadcrumb';
import UserNew from '../../components/user/UserNew';
import moment from 'moment-timezone';

export default function UserListPage() {
    const [users, setUsers] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [paginationData, setPaginationData] = React.useState(null);
    const [updateModalVisible, setUpdateModalVisible] = React.useState(false);
    const [disabledModalVisible, setDisabledModalVisible] = React.useState(false);
    const [selectedUser, setSelectedUser] = React.useState(null);
    const { check, currentUser } = useUserContext();
    const navigate = useNavigate();
    const [loadingMore, setLoadingMore] = React.useState(false);
    // champs filtrage
    const [searchText, setSearchText] = React.useState(null);
    const [groupeIds, setGroupeIds] = React.useState([]);
    const [statut, setStatut] = React.useState('tout');
    const [groupes, setGroupes] = React.useState([]);
    const [isUserStateChanging, setIsUserStateChanging] = React.useState(false);
    const [newModalVisible, setNewModalVisible] = React.useState(false);


    const init = useCallback(() => {
        setLoading(true);
        UserService.getInstance()
            .search({ searchText, groupeIds, statut })
            .then((response) => {
                setPaginationData(response.data);
                setUsers(response.data.data);
            }).catch((error) => {
                catchError(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [searchText, groupeIds, statut]);

    React.useEffect(() => {
        if (!check('VIEW-USERS')) {
            return;
        }
        init();
    }, [init, check]);

    React.useEffect(() => {
        GroupeService.getInstance()
            .all()
            .then((response) => {
                setGroupes(response.data);
            }).catch((error) => {
                catchError(error);
            });
    }, []);

    const handleEnable = (user) => {
        setIsUserStateChanging(true);
        UserService.getInstance()
            .enable(user.uid)
            .then((response) => {
                Toast.success(response.message);
                setUsers(users => users.map((u) => {
                    if (u.uid === user.uid) {
                        u.enabled = true;
                    }
                    return u;
                }));
            }).catch((error) => {
                catchError(error);
            })
            .finally(() => {
                setIsUserStateChanging(false);
            });
    }

    const handleDelete = (idOrUid) => {
        setLoading(true);
        UserService.getInstance()
            .remove(idOrUid)
            .then((response) => {
                Toast.success(response.message);
                setUsers(users => users.filter((user) => user.uid !== idOrUid));
            }).catch((error) => {
                catchError(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const openEditModal = (user) => {
        setSelectedUser(user);
        setUpdateModalVisible(true);
    }
    const openDisabledModal = (user) => {
        setSelectedUser(user);
        setDisabledModalVisible(true);
    }
    const loadMore = () => {
        if (paginationData.next_page_url) {
            setLoadingMore(true);
            UserService
                .post(paginationData.next_page_url, { searchText, groupeIds, statut })
                .then((response) => {
                    if (!response) {
                        return;
                    }
                    setPaginationData(response.data);
                    setUsers([...users, ...response.data.data]);
                }).catch((error) => {
                    catchError(error);
                })
                .finally(() => {
                    setLoadingMore(false);
                });
        }
    }

    const gotoUserDetails = (user) => {
        navigate(`/user/${user.uid}`);
    }

    const columns = [
        {
            title: "Avatar",
            dataIndex: 'avatar',
            key: 'avatar',
            render: (avatar) => <Avatar size={32} src={avatar} />,
            width: 80,
            fixed: 'left',
        },
        {
            title: 'Nom',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            width: 200,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: (a, b) => a.email.localeCompare(b.email),
            width: 300,
        },
        {
            title: 'Téléphone',
            dataIndex: 'telephone',
            key: 'telephone',
            width: 150,
            render: (telephone) => telephone ? telephone : 'N/A',
        },
        {
            title: 'Statut',
            dataIndex: 'enabled',
            key: 'enabled',
            render: (enabled) => enabled ? <Tag color="green">Activé</Tag> : <Tag color="red">Désactivé</Tag>,
            width: 100,
        },
        {
            title: 'Groupes',
            dataIndex: 'groupes',
            key: 'groupes',
            render: (groupes) => (
                <ul className='list-group'>
                    {groupes.map((group, index) => (
                        <li key={group.uid} className='list-group-item'>
                            <Tag key={index} color="blue">{group.nom}</Tag>
                        </li>
                    ))}
                </ul>
            ),
            width: 200,
        },
        {
            title: "Personnel",
            dataIndex: 'personnel_id',
            key: 'personnel_id',
            render: (personnel) => personnel ? <Badge status="success" text="Oui" /> : <Badge status="error" text="Non" />,
            width: 150,
        },
        {
            title: "Créé le",
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (created_at) => <Moment format="DD/MM/YYYY HH:mm">{created_at}</Moment>,
        },
        {
            title: "Dernière connexion",
            dataIndex: 'last_login_at',
            key: 'last_login_at',
            width: 200,
           render: (last_login) => last_login ? moment(last_login).fromNow() : 'N/A',
        },
        {
            title: "Dernière activité",
            dataIndex: 'last_activity_at',
            key: 'last_activity_at',
            width: 200,
            render: (last_activity) => last_activity ?  moment(last_activity).fromNow() : 'N/A',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text, user) => (
                <Space>
                    {check('VIEW-USER') ?
                        <Button onClick={() => gotoUserDetails(user)} type='primary' icon={<EyeFilled />}></Button>
                        : null}
                    {check('EDIT-USER') ?
                        <Button className='btn-warning' onClick={() => openEditModal(user)} icon={<EditFilled />}></Button>
                        : null}
                    {user.enabled ? (
                        check('DISABLE-USER') ? (
                            <Button danger loading={isUserStateChanging} onClick={() => openDisabledModal(user)} icon={<LockFilled />}></Button>
                        ) : <></>
                    ) : (
                        check('ENABLE-USER') ? (
                            <Popconfirm title="Voulez-vous vraiment activer cet uilisateur ?" onConfirm={() => handleEnable(user)} okText="Activer" cancelText="Annuler" okType='primary'>
                                <Button type='dashed' loading={isUserStateChanging} icon={<UnlockFilled title='Activer un utilisateur' />}></Button>
                            </Popconfirm>
                        ) : <></>)
                    }
                    {check('DELETE-USER') && !user.is_admin ? <Popconfirm okType='danger' title="Voulez-vous vraiment supprimer cet utilisateur?" onConfirm={() => handleDelete(user.uid)} okText="Confirmer" cancelText="Annuler">
                        <Button type='primary' danger icon={<DeleteFilled />}></Button>
                    </Popconfirm> : null}
                </Space>
            ),
            width: 220,
        },
    ];

    const handleCreation = (user) => {
        setUsers([user, ...users]);
    }

    const handleUpdate = (user) => {
        setUsers(users.map((u) => {
            if (u.uid === user.uid) {
                u.name = user.name;
                return u;
            }
            return u;
        }));
        setSelectedUser(null);
        setUpdateModalVisible(false);
    }

    const handleDisabled = (user) => {
        setUsers(users.map((u) => {
            if (u.uid === user.uid) {
                u.enabled = user.enabled;
                return u;
            }
            return u;
        }));
        setSelectedUser(null);
        setDisabledModalVisible(false);
    }

    const labelStyle = {
        marginRight: '10px',
        display: 'flex',
        alignItems: 'center',
    };

    const containerStyle = {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
    };

    return (
        <Main>
            <Breadcrumb parentLink="/" canSee={check('VIEW-DASHBOARD')} parentLinkTitle="Accueil" title="Utilisateurs" />
            {check('VIEW-USERS') ? <>
                <PageTitle title="Liste des utilisateurs" subTitle="Les comptes sont créés à partir de la liste du personnel" icon={<UsergroupAddOutlined />}>
                    {
                        currentUser?.is_admin ?
                            <Button type="primary" icon={<PlusCircleFilled />} onClick={() => setNewModalVisible(true)}>Ajouter un admin</Button>
                            : null

                    }
                    <Button type='default' icon={<ReloadOutlined />} onClick={init}>Rafraîchir</Button>
                </PageTitle>
                <Spin spinning={loading}>
                    <InfiniteTable rowKey={'uid'} dataSource={users} paginationData={paginationData} loadMore={loadMore} loading={loadingMore}
                        columns={columns} title={
                            <Row gutter={[24, 16]}>
                                <Col span={24}>
                                </Col>
                                <Col span={24}>
                                    <Row gutter={[16, 16]} justify={'center'}>
                                        <Col xs={24} md={8} lg={6}>
                                            <Input.Search value={searchText} onInput={
                                                (e) => {
                                                    setSearchText(e.target.value);
                                                }} placeholder="Rechercher un utilisateur" />
                                        </Col>
                                        <Col xs={24} md={8} lg={6}>
                                            <div style={containerStyle}>
                                                <div style={labelStyle}>Groupe(s):</div>
                                                <Select value={groupeIds} onChange={
                                                    (value) => {
                                                        setGroupeIds(value);
                                                    }
                                                }
                                                    allowClear mode='multiple' placeholder="Filtrer par groupe" style={{ width: '100%' }}>
                                                    {groupes.map((groupe) => (
                                                        <Select.Option key={groupe.id} value={groupe.id}>{groupe.nom}</Select.Option>
                                                    ))}
                                                </Select>
                                            </div>
                                        </Col>
                                        <Col xs={24} md={8} lg={6}>
                                            <div style={containerStyle}>
                                                <div style={labelStyle}>Statut:</div>
                                                <Select value={statut} onChange={
                                                    (value) => {
                                                        setStatut(value);
                                                    }
                                                } placeholder="Filtrer par statut" style={{ width: '100%' }}>
                                                    <Select.Option value="tout">Tous</Select.Option>
                                                    <Select.Option value="actif">Activé</Select.Option>
                                                    <Select.Option value="inactif">Désactivé</Select.Option>
                                                </Select>
                                            </div>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        }
                    />
                </Spin>
                <UserNew
                    visible={newModalVisible}
                    onCreate={handleCreation}
                    onCancel={() => setNewModalVisible(false)}
                />
                {selectedUser ? <UserEdit
                    visible={updateModalVisible}
                    uid={selectedUser.uid}
                    onUpdate={handleUpdate}
                    onCancel={() => setUpdateModalVisible(false)}
                /> : null}
                {selectedUser ? <DisableUserModal
                    visible={disabledModalVisible}
                    uid={selectedUser.uid}
                    onUpdate={handleDisabled}
                    onCancel={() => setDisabledModalVisible(false)}
                /> : null}
            </> : <UnauthorizedMessage />}
        </Main>
    );
}